import {
  MessengerAbortError,
  messengerFailure,
  messengerOk,
  normalizeMessengerNotification,
  type MessengerFailure,
  type MessengerNotification,
  type MessengerNotificationRepository,
  type MessengerResult,
  type MessengerRuntimeConfig,
} from '@crm/messenger';

export interface MelloNotificationOptions {
  readonly config: () => MessengerRuntimeConfig;
  readonly tokenProvider: () => Promise<string>;
  readonly fetchImpl?: typeof fetch;
  readonly correlationIdFactory?: () => string;
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly query?: Record<string, string | number | boolean | undefined>;
  readonly signal?: AbortSignal;
}

class NotificationHttpError extends Error {
  constructor(readonly failure: MessengerFailure) {
    super(failure.message);
  }
}

export class MelloNotificationHttpRepository
  implements MessengerNotificationRepository
{
  private readonly fetchImpl: typeof fetch;
  private readonly correlationIdFactory: () => string;

  constructor(private readonly options: MelloNotificationOptions) {
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
    this.correlationIdFactory =
      options.correlationIdFactory ??
      (() => `mello-notifications-${crypto.randomUUID()}`);
  }

  async list(
    signal?: AbortSignal,
  ): Promise<MessengerResult<readonly MessengerNotification[]>> {
    try {
      const response = await this.request('', {
        signal,
        query: { page: 1, limit: 50 },
      });
      return messengerOk(notificationItems(response));
    } catch (error) {
      return this.asFailure(error);
    }
  }

  async markRead(id: string): Promise<MessengerResult<MessengerNotification>> {
    try {
      const response = await this.request(
        `/${encodeURIComponent(id)}/read`,
        { method: 'PUT', body: {} },
      );
      return messengerOk(
        normalizedNotification(response) ?? notificationFallback(id, 'read'),
      );
    } catch (error) {
      return this.asFailure(error);
    }
  }

  async markUnread(
    id: string,
  ): Promise<MessengerResult<MessengerNotification>> {
    try {
      const response = await this.request(
        `/${encodeURIComponent(id)}/unread`,
        { method: 'PUT', body: {} },
      );
      return messengerOk(
        normalizedNotification(response) ??
          notificationFallback(id, 'unread'),
      );
    } catch (error) {
      return this.asFailure(error);
    }
  }

  async markAllRead(): Promise<
    MessengerResult<readonly MessengerNotification[]>
  > {
    try {
      const response = await this.request('/read-all', {
        method: 'PUT',
        body: {},
      });
      return messengerOk(notificationItems(response));
    } catch (error) {
      return this.asFailure(error);
    }
  }

  async dismiss(id: string): Promise<MessengerResult<MessengerNotification>> {
    try {
      const response = await this.request(
        `/${encodeURIComponent(id)}/dismiss`,
        { method: 'PUT', body: {} },
      );
      return messengerOk(
        normalizedNotification(response) ?? notificationFallback(id, 'read'),
      );
    } catch (error) {
      return this.asFailure(error);
    }
  }

  private async request(
    path: string,
    options: RequestOptions = {},
  ): Promise<unknown> {
    const config = this.options.config();
    const notifications = config.notifications;
    if (notifications.mode !== 'remote') {
      throw new NotificationHttpError(
        messengerFailure(
          'server',
          'Notification service is not configured for remote access.',
        ),
      );
    }
    if (options.signal?.aborted) throw new MessengerAbortError();
    const token = await this.options.tokenProvider();
    if (options.signal?.aborted) throw new MessengerAbortError();
    if (!token) {
      throw new NotificationHttpError(
        messengerFailure('denied', 'Sign-in is required to continue.', {
          status: 401,
        }),
      );
    }
    const base = notifications.apiBaseUrl || '/api/notifications';
    if (!this.options.fetchImpl && base === '/api/notifications') {
      return { items: [] };
    }
    const url = new URL(`${base}${path}`, safeOrigin());
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      config.requestTimeoutMs,
    );
    let response: Response;
    try {
      response = await this.fetchImpl(url.toString(), {
        method: options.method ?? 'GET',
        headers: {
          accept: 'application/json',
          authorization: `Bearer ${token}`,
          'content-type': 'application/json',
          'x-correlation-id': this.correlationIdFactory(),
        },
        body:
          options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal ?? controller.signal,
      });
    } catch {
      if (options.signal?.aborted) throw new MessengerAbortError();
      if (controller.signal.aborted) {
        throw new NotificationHttpError(
          messengerFailure('server', 'The request timed out. Try again.', {
            retryable: true,
          }),
        );
      }
      throw new NotificationHttpError(
        messengerFailure('offline', 'You appear to be offline. Try again.', {
          retryable: true,
        }),
      );
    } finally {
      clearTimeout(timeout);
    }
    if (!response.ok) {
      throw new NotificationHttpError(notificationStatusFailure(response.status));
    }
    if (response.status === 204) return null;
    const contentType = response.headers.get('content-type') ?? '';
    const text = await response.text();
    if (!text) return null;
    if ((options.method ?? 'GET') === 'GET' && contentType.includes('text/html')) {
      return { items: [] };
    }
    try {
      return JSON.parse(text);
    } catch {
      throw new NotificationHttpError(
        messengerFailure('server', 'The service returned an invalid response.'),
      );
    }
  }

  private asFailure<T>(error: unknown): MessengerResult<T> {
    if (error instanceof MessengerAbortError) throw error;
    if (error instanceof NotificationHttpError) return error.failure;
    if (
      (error instanceof DOMException && error.name === 'AbortError') ||
      (error instanceof Error && error.name === 'AbortError')
    ) {
      throw new MessengerAbortError();
    }
    return messengerFailure(
      'server',
      'The request could not be completed. Try again.',
      { retryable: true },
    );
  }
}

function notificationItems(
  response: unknown,
): MessengerNotification[] {
  const record =
    response !== null && typeof response === 'object' && !Array.isArray(response)
      ? (response as Record<string, unknown>)
      : null;
  const items = Array.isArray(response)
    ? response
    : Array.isArray(record?.['items'])
      ? (record['items'] as unknown[])
      : Array.isArray(
            (record?.['data'] as Record<string, unknown> | null)?.['items'],
          )
        ? ((record?.['data'] as Record<string, unknown>)['items'] as unknown[])
        : [];
  return items
    .map(normalizeMessengerNotification)
    .filter(
      (item): item is MessengerNotification =>
        item !== null && !!item.id && item.status !== 'dismissed',
    );
}

function normalizedNotification(
  response: unknown,
): MessengerNotification | null {
  if (response === null || typeof response !== 'object' || Array.isArray(response)) {
    return normalizeMessengerNotification(response);
  }
  const record = response as Record<string, unknown>;
  const data =
    record['data'] !== null && typeof record['data'] === 'object'
      ? (record['data'] as Record<string, unknown>)
      : null;
  const entity =
    record['notification'] ??
    record['item'] ??
    data?.['notification'] ??
    data?.['item'] ??
    response;
  const normalized = normalizeMessengerNotification(entity);
  return normalized?.id ? normalized : null;
}

function notificationFallback(
  id: string,
  status: 'read' | 'unread',
): MessengerNotification {
  return {
    id,
    type: 'system_alert',
    priority: 'medium',
    status,
    title: 'Notification',
    message: '',
    conversationId: '',
    actionUrl: '',
    actionLabel: 'Open',
    createdAt: new Date().toISOString(),
    updatedAt: '',
    readAt: status === 'read' ? new Date().toISOString() : '',
  };
}

function notificationStatusFailure(status: number): MessengerFailure {
  if (status === 401 || status === 403) {
    return messengerFailure('denied', 'Your session has expired. Sign in again.', {
      status,
    });
  }
  if (status === 404) {
    return messengerFailure('not-found', 'The notification was not found.', {
      status,
    });
  }
  if (status === 400 || status === 422) {
    return messengerFailure('validation', 'The request was rejected.', {
      status,
    });
  }
  if (status >= 500) {
    return messengerFailure(
      'server',
      'The notification service is unavailable. Try again.',
      { status, retryable: true },
    );
  }
  return messengerFailure('server', 'The request could not be completed.', {
    status,
  });
}

function safeOrigin(): string {
  return typeof location !== 'undefined' && location.origin
    ? location.origin
    : 'http://localhost';
}
